/**
 * 贪吃猫狗的画布绘制（Canvas 2D）。只画，不改状态：给规则核心的状态快照、缩放、配色、TA 的图与飘字，画一帧。
 * 颜色全从主题令牌读（readPalette），深色模式跟着变；按钮、分数、暂停、结算都在 DOM 里，不画在画布上。
 */
import type { Food, Gate, Rect, SnakeState, Yarn } from "../core";
import { FLOAT_MS, floatAlpha, type FloatText } from "./feedback";
import { spritePlacement, type SpriteSpec } from "./sprite";
import type { Fit } from "./viewport";

export interface Palette {
  floor: string;
  grid: string;
  wall: string;
  gate: string;
  crate: string;
  crateLine: string;
  treat: string;
  leaf: string;
  yarn: string;
  pet: string;
  ink: string;
  shadow: string;
  good: string;
  bad: string;
  info: string;
}

/** 令牌 → 画布颜色；读不到（比如测试环境）就用浅色主题的值 */
const TOKENS: Record<keyof Palette, [string, string]> = {
  floor: ["--c-surface-2", "#f9f3e8"],
  grid: ["--c-surface-stroke", "rgba(74, 56, 34, 0.1)"],
  wall: ["--c-ink-2", "#6e6457"],
  gate: ["--c-sky", "#6aa9dc"],
  crate: ["--c-sun", "#e9a53a"],
  crateLine: ["--c-ink-2", "#6e6457"],
  treat: ["--c-coral", "#df6f55"],
  leaf: ["--c-leaf", "#2f7d5e"],
  yarn: ["--c-sky", "#6aa9dc"],
  pet: ["--c-sun", "#e9a53a"],
  ink: ["--c-ink", "#2b2620"],
  shadow: ["--c-shadow", "rgba(32, 49, 41, 0.14)"],
  good: ["--c-leaf", "#2f7d5e"],
  bad: ["--c-danger", "#c24b3a"],
  info: ["--c-ink-2", "#6e6457"],
};

export function readPalette(element: Element | null): Palette {
  const style = element && typeof getComputedStyle === "function" ? getComputedStyle(element) : null;
  const out = {} as Palette;
  for (const [key, [token, fallback]] of Object.entries(TOKENS) as [keyof Palette, [string, string]][]) {
    out[key] = style?.getPropertyValue(token).trim() || fallback;
  }
  return out;
}

export interface SceneInput {
  state: SnakeState;
  fit: Fit;
  dpr: number;
  palette: Palette;
  /** TA 的图（已加载）；null＝画兜底的圆点小脸 */
  sprite: { spec: SpriteSpec; image: CanvasImageSource } | null;
  floats: readonly FloatText[];
  /** 指针给的目标点（画个小圈提示往哪走）；null 不画 */
  target: { x: number; y: number } | null;
  nowMs: number;
  reducedMotion: boolean;
}

/** 无敌时间里的闪烁：减少动态效果时改成固定半透明 */
export function petAlpha(invulnerableMs: number, nowMs: number, reducedMotion: boolean): number {
  if (invulnerableMs <= 0) return 1;
  if (reducedMotion) return 0.55;
  return Math.floor(nowMs / 120) % 2 === 0 ? 0.35 : 0.9;
}

function roundRect(ctx: CanvasRenderingContext2D, r: Rect, radius: number) {
  ctx.beginPath();
  ctx.roundRect(r.x, r.y, r.w, r.h, radius);
}

function drawWall(ctx: CanvasRenderingContext2D, wall: Rect, p: Palette) {
  ctx.fillStyle = p.wall;
  roundRect(ctx, wall, 4);
  ctx.fill();
}

function drawGate(ctx: CanvasRenderingContext2D, gate: Gate, p: Palette) {
  // 窄口：虚线框出开口，提醒「小个子才过得去」
  ctx.save();
  ctx.strokeStyle = p.gate;
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 4]);
  ctx.strokeRect(gate.x + 1, gate.y + 1, gate.w - 2, gate.h - 2);
  ctx.restore();
}

function drawCrate(ctx: CanvasRenderingContext2D, crate: Rect, p: Palette) {
  ctx.fillStyle = p.crate;
  roundRect(ctx, crate, 3);
  ctx.fill();
  ctx.strokeStyle = p.crateLine;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(crate.x + 4, crate.y + 4);
  ctx.lineTo(crate.x + crate.w - 4, crate.y + crate.h - 4);
  ctx.moveTo(crate.x + crate.w - 4, crate.y + 4);
  ctx.lineTo(crate.x + 4, crate.y + crate.h - 4);
  ctx.stroke();
}

function drawFood(ctx: CanvasRenderingContext2D, food: Food, p: Palette) {
  ctx.fillStyle = food.kind === "treat" ? p.treat : p.leaf;
  ctx.beginPath();
  if (food.kind === "treat") ctx.arc(food.x, food.y, 8, 0, Math.PI * 2);
  else ctx.ellipse(food.x, food.y, 9, 5, -Math.PI / 5, 0, Math.PI * 2);
  ctx.fill();
}

function drawYarn(ctx: CanvasRenderingContext2D, yarn: Yarn, p: Palette) {
  ctx.fillStyle = p.yarn;
  ctx.beginPath();
  ctx.arc(yarn.x, yarn.y, yarn.radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = p.floor;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(yarn.x, yarn.y, yarn.radius * 0.55, 0.3, Math.PI * 1.4);
  ctx.stroke();
}

function drawPet(ctx: CanvasRenderingContext2D, input: SceneInput) {
  const { state, palette: p, sprite } = input;
  const pet = state.pet;
  ctx.save();
  ctx.globalAlpha = petAlpha(pet.invulnerableMs, input.nowMs, input.reducedMotion);
  // 地上的影子
  ctx.fillStyle = p.shadow;
  ctx.beginPath();
  ctx.ellipse(pet.x, pet.y + pet.radius * 0.75, pet.radius * 0.95, pet.radius * 0.35, 0, 0, Math.PI * 2);
  ctx.fill();
  if (sprite) {
    const at = spritePlacement(sprite.spec, pet);
    ctx.drawImage(sprite.image, at.x, at.y, at.width, at.height);
  } else {
    // 兜底：圆点小脸，眼睛朝着走的方向
    ctx.fillStyle = p.pet;
    ctx.beginPath();
    ctx.arc(pet.x, pet.y, pet.radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = p.ink;
    const ex = Math.cos(pet.facing) * pet.radius * 0.35;
    const ey = Math.sin(pet.facing) * pet.radius * 0.35;
    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.arc(pet.x + ex - Math.sin(pet.facing) * side * pet.radius * 0.35, pet.y + ey + Math.cos(pet.facing) * side * pet.radius * 0.35, Math.max(2, pet.radius * 0.12), 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

/** 画一条飘字（贪吃、篮球共用）：往上飘、前段不透明后段淡出；减少动态效果时原地淡出 */
export function drawFloat(ctx: CanvasRenderingContext2D, f: FloatText, at: { nowMs: number; reducedMotion: boolean; palette: Palette }) {
  const age = Math.min(1, Math.max(0, (at.nowMs - f.bornMs) / FLOAT_MS));
  const rise = at.reducedMotion ? 0 : age * 24;
  ctx.save();
  ctx.globalAlpha = floatAlpha(age);
  ctx.fillStyle = f.tone === "good" ? at.palette.good : f.tone === "bad" ? at.palette.bad : at.palette.info;
  ctx.font = "700 15px system-ui, -apple-system, \"PingFang SC\", \"Microsoft YaHei\", sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  // 先描一圈地面色的边：飘字压在墙、纸箱上也看得清
  ctx.lineJoin = "round";
  ctx.lineWidth = 4;
  ctx.strokeStyle = at.palette.floor;
  ctx.strokeText(f.text, f.x, f.y - rise);
  ctx.fillText(f.text, f.x, f.y - rise);
  ctx.restore();
}

export function drawScene(ctx: CanvasRenderingContext2D, input: SceneInput): void {
  const { state, fit, dpr, palette: p } = input;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.setTransform(dpr * fit.scale, 0, 0, dpr * fit.scale, dpr * fit.offsetX, dpr * fit.offsetY);
  const width = fit.scale > 0 ? fit.width / fit.scale : 0;
  const height = fit.scale > 0 ? fit.height / fit.scale : 0;
  // 地面与浅网格
  ctx.fillStyle = p.floor;
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = p.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = 40; x < width; x += 40) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
  }
  for (let y = 40; y < height; y += 40) {
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
  }
  ctx.stroke();
  for (const wall of state.walls) drawWall(ctx, wall, p);
  for (const gate of state.gates) drawGate(ctx, gate, p);
  for (const crate of state.crates) drawCrate(ctx, crate, p);
  for (const food of state.foods) drawFood(ctx, food, p);
  if (input.target) {
    ctx.strokeStyle = p.info;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(input.target.x, input.target.y, 7, 0, Math.PI * 2);
    ctx.stroke();
  }
  for (const yarn of state.yarns) drawYarn(ctx, yarn, p);
  drawPet(ctx, input);
  for (const f of input.floats) drawFloat(ctx, f, input);
}
